import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { WorkflowVariableDefinitionInfo } from '@blaze-shared/api-contracts-domain';
import { ProjectContextFacade } from '@blaze-shared/app-state-data-access';
import { UrlVariable } from '@blaze-shared/projects-domain';
import { DEFAULT_ACCOUNT_VALUE, VarAccount } from '@blaze-shared/schema-domain';
import { VariablesFacade } from '@blaze-shared/variables-data-access';

import { IntelliContext } from './models/intellisense';

@Injectable({ providedIn: 'root' })
export class IntelliContextService {
  private readonly projectContextFacade = inject(ProjectContextFacade);
  private readonly variablesFacade = inject(VariablesFacade);

  private ctx: IntelliContext = {};
  private ctx$ = new BehaviorSubject<IntelliContext>({}); // value context observable
  private variables: WorkflowVariableDefinitionInfo[] = [];
  private urlVariables: UrlVariable[] = [];

  constructor() {
    // Make sure to populate context for account even if no VarAccount has been found for the current user so that
    // root and admin users can still add appropriate conditions involving CurrentAccount.
    this.projectContextFacade.accountsSchema$.subscribe(() => this.createContext());
  }

  /**
   * Creates an object containing all possible values, functions, constants user can use in queries, filters, etc.
   */
  createContext(): IntelliContext {
    let ctx: IntelliContext = {};

    ctx = this.updateAccountContext(ctx);
    ctx = this.updateUserContext(ctx);
    ctx = this.updateVariablesContext(ctx);
    ctx = this.updateUrlVariablesContext(ctx);

    this.ctx = ctx;
    this.ctx$.next(this.ctx);
    return ctx;
  }

  getContextForInput(input: unknown): IntelliContext {
    if (!input) {
      return this.ctx;
    }
    return this.ctx;
  }

  observeContext(): Observable<IntelliContext> {
    return this.ctx$.asObservable();
  }

  updateUrlVariables(urlVariables: UrlVariable[]): void {
    this.urlVariables = urlVariables;
    this.createContext();
  }

  private updateAccountContext(ctx: IntelliContext): IntelliContext {
    let varAccount: VarAccount = this.projectContextFacade.varAccount || {
      ...DEFAULT_ACCOUNT_VALUE,
    };
    const varAccountSchema = this.projectContextFacade.accountsSchema;
    if (!varAccount && varAccountSchema) {
      varAccount = { ...DEFAULT_ACCOUNT_VALUE };
      for (const col of varAccountSchema.columns) {
        varAccount[col.name] = undefined;
      }
    }
    return { ...ctx, account: varAccount ?? { ...DEFAULT_ACCOUNT_VALUE } };
  }

  private updateUserContext(ctx: IntelliContext): IntelliContext {
    const varUser = this.projectContextFacade.varUser;
    return { ...ctx, user: varUser };
  }

  private updateUrlVariablesContext(ctx: IntelliContext): IntelliContext {
    return { ...ctx, urlVariables: this.urlVariables };
  }

  private updateVariablesContext(ctx: IntelliContext): IntelliContext {
    this.variables = this.variablesFacade.varDefs();
    return {
      ...ctx,
      vars: this.variables,
    };
  }
}
